import logoSvg from "@/assets/logo.svg";
import { useUserRole } from "@/hooks/useUserRole";
import { storyElement } from "@/lib/story-element";
import { Badge } from "@/components/ui/badge";
import { Loader2, Swords, Hammer, Globe, Trash2, ShieldCheck } from "lucide-react";
import AppHeader from "@/components/layout/AppHeader";
import { useIndexData } from "@/hooks/useIndexData";
import { useLocale } from "@/hooks/useLocale";
import { usePageMeta } from "@/hooks/usePageMeta";

// Extracted tab components
import { BuildTab } from "@/components/index/BuildTab";
import { PlayTab } from "@/components/index/PlayTab";
import { PublicWorldsTab, TrashTab, AdminWorldsTab } from "@/components/index/OtherTabs";
import { StartGameDialog } from "@/components/index/StartGameDialog";

const Index = () => {
  const data = useIndexData();
  const { t } = useLocale();
  // W8: add page-level meta so the dashboard has accurate title/description
  // rather than inheriting the static index.html fallback.
  usePageMeta({
    title: "Dashboard — Adventure Venture",
    description: "Your worlds, adventures, and player characters. Build custom AI-powered RPG worlds and play in them with friends.",
    canonical: "https://adventureventure.app/",
    noindex: true, // dashboard is logged-in only
  });
  const { isSuperAdmin } = useUserRole();
  const {
    worlds, sharedWorlds, adventures, joinedAdventures,
    publicWorlds, trashedWorlds, allWorlds, adventureWorlds, worldStats,
    isLoading, activeTab, setActiveTab,
    currentUserId, cloningWorldId,
    playableWorlds,
    worldCredits, adventureCredits,
    showNewWorld, setShowNewWorld,
    creationMode, setCreationMode,
    newWorldName, setNewWorldName,
    newWorldDesc, setNewWorldDesc,
    newWorldPrompt, setNewWorldPrompt,
    contextFiles, setContextFiles,
    creating, fileInputRef,
    showNewGame, setShowNewGame,
    selectedWorldId, setSelectedWorldId,
    adventureName, setAdventureName,
    isMultiplayer, setIsMultiplayer,
    turnMode, setTurnMode,
    creatingGame, setCreatingGame,
    showJoin, setShowJoin,
    joinCode, setJoinCode,
    joining, copiedId,
    handleFileUpload, handleCreateWorld,
    handleDeleteWorld, handleRestoreWorld, handlePermanentDeleteWorld,
    handlePublishWorld, handleCloneWorld,
    handleDeleteAdventure, handleLeaveAdventure,
    handleStartGame, handleJoinGame,
    copyInviteLink, navigate,
  } = data;

  return (
    <>
      <AppHeader />
      <div {...storyElement("page-index")} className="min-h-[calc(100vh-3.5rem)] bg-background">
        {/* Hero */}
        <div className="text-center py-8 sm:py-12 px-4 sm:px-6 space-y-3 sm:space-y-4">
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary opacity-20 blur-3xl animate-glow" />
            <img src={logoSvg} alt="Adventure Venture" className="h-14 sm:h-20 mx-auto relative z-10" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary via-[hsl(var(--candleglow))] to-secondary break-words">
            Adventure Venture
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">{t.idx_tagline}</p>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
          {/* Tab Switcher */}
          <div className="flex items-center border-b border-border mb-8 overflow-x-auto">
            <div className="flex items-center gap-1 flex-1">
              {[
                { id: "build", icon: Hammer, label: t.idx_my_worlds, activeClass: "text-primary border-primary" },
                { id: "play", icon: Swords, label: t.idx_my_adventures, activeClass: "text-secondary border-secondary" },
                { id: "public", icon: Globe, label: t.idx_public_worlds, activeClass: "text-[hsl(var(--candleglow))] border-[hsl(var(--candleglow))]" },
              ].map(({ id, icon: Icon, label, activeClass }) => (
                <button key={id} onClick={() => setActiveTab(id as any)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                    activeTab === id ? activeClass : "text-muted-foreground border-transparent hover:text-foreground"
                  }`}>
                  <Icon className="w-4 h-4" />{label}
                </button>
              ))}
              {isSuperAdmin && (
                <button onClick={() => setActiveTab("admin")}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                    activeTab === "admin" ? "text-destructive border-destructive" : "text-muted-foreground border-transparent hover:text-foreground"
                  }`}>
                  <ShieldCheck className="w-4 h-4" />{t.idx_all_worlds}
                  <Badge variant="secondary" className="text-[10px] h-5 px-1.5">{allWorlds.length}</Badge>
                </button>
              )}
            </div>
            {trashedWorlds.length > 0 && (
              <button onClick={() => setActiveTab("trash")}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ml-auto ${
                  activeTab === "trash" ? "text-destructive border-destructive" : "text-muted-foreground border-transparent hover:text-foreground"
                }`}>
                <Trash2 className="w-4 h-4" />{t.idx_trash}
                <Badge variant="secondary" className="text-[10px] h-5 px-1.5">{trashedWorlds.length}</Badge>
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : activeTab === "build" ? (
            <BuildTab t={t} worlds={worlds} sharedWorlds={sharedWorlds} showNewWorld={showNewWorld} setShowNewWorld={setShowNewWorld}
              creationMode={creationMode} setCreationMode={setCreationMode} newWorldName={newWorldName} setNewWorldName={setNewWorldName}
              newWorldDesc={newWorldDesc} setNewWorldDesc={setNewWorldDesc} newWorldPrompt={newWorldPrompt} setNewWorldPrompt={setNewWorldPrompt}
              contextFiles={contextFiles} setContextFiles={setContextFiles} creating={creating} fileInputRef={fileInputRef}
              handleFileUpload={handleFileUpload} handleCreateWorld={handleCreateWorld} handleDeleteWorld={handleDeleteWorld}
              handleCloneWorld={handleCloneWorld} handlePublishWorld={handlePublishWorld} navigate={navigate}
              setShowNewGame={setShowNewGame} setSelectedWorldId={setSelectedWorldId} worldStats={worldStats} worldCredits={worldCredits}
              cloningWorldId={cloningWorldId} />
          ) : activeTab === "play" ? (
            <PlayTab t={t} adventures={adventures} joinedAdventures={joinedAdventures} worlds={worlds} sharedWorlds={sharedWorlds}
              adventureWorlds={adventureWorlds} playableWorlds={playableWorlds} showNewGame={showNewGame} setShowNewGame={setShowNewGame} selectedWorldId={selectedWorldId}
              setSelectedWorldId={setSelectedWorldId} adventureName={adventureName} setAdventureName={setAdventureName}
              isMultiplayer={isMultiplayer} setIsMultiplayer={setIsMultiplayer} turnMode={turnMode} setTurnMode={setTurnMode}
              creatingGame={creatingGame} setCreatingGame={setCreatingGame} handleStartGame={handleStartGame} showJoin={showJoin}
              setShowJoin={setShowJoin} joinCode={joinCode} setJoinCode={setJoinCode} joining={joining} handleJoinGame={handleJoinGame}
              copiedId={copiedId} copyInviteLink={copyInviteLink} navigate={navigate} currentUserId={currentUserId}
              handleDeleteAdventure={handleDeleteAdventure} handleLeaveAdventure={handleLeaveAdventure} adventureCredits={adventureCredits} />
          ) : activeTab === "admin" && isSuperAdmin ? (
            <AdminWorldsTab allWorlds={allWorlds} navigate={navigate} worldStats={worldStats} setShowNewGame={setShowNewGame}
              setSelectedWorldId={setSelectedWorldId} onClone={handleCloneWorld} cloningWorldId={cloningWorldId} />
          ) : activeTab === "trash" ? (
            <TrashTab t={t} trashedWorlds={trashedWorlds} onRestore={handleRestoreWorld} onPermanentDelete={handlePermanentDeleteWorld} navigate={navigate} />
          ) : (
            <PublicWorldsTab t={t} publicWorlds={publicWorlds} cloningWorldId={cloningWorldId} navigate={navigate}
              onClone={handleCloneWorld} setShowNewGame={setShowNewGame} setSelectedWorldId={setSelectedWorldId} worldStats={worldStats} />
          )}
        </div>

        <StartGameDialog t={t} showNewGame={showNewGame} setShowNewGame={setShowNewGame} selectedWorldId={selectedWorldId}
          setSelectedWorldId={setSelectedWorldId} playableWorlds={playableWorlds} adventures={adventures}
          joinedAdventures={joinedAdventures} creatingGame={creatingGame} setCreatingGame={setCreatingGame} navigate={navigate} />
      </div>
    </>
  );
};

export default Index;
